import { pageConfig } from '@components/OrderMovements/OrderMovements/MovementDetail/BOL/Detail/constants'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useQueryClient } from '@tanstack/react-query'
import { Divider, Select } from 'antd'
import { Horizontal } from '@gravitate-js/excalibrr'
import { DateRangeFilter } from '../../../../../components/_SiteWide/DateRangeFilter'
import type { DayRange } from '@/hooks/useDateRangeFilter'
import type { Filter } from '@gravitate-js/excalibrr/dist/components/Grid/SearchGridHeader/DynamicFilterForm/types'
import type { OrderManagementGridView } from '@/pages/OrderManagement/All/types'
import { OrderTankToggle } from './OrderTankToggle'

export type DefaultActionBtnsProps = {
  orderStatus: string
  onStatusChange: (value: string) => void
  filters: Filter
  setFilters: (filters: Filter) => void
  excelOptions: Gravitate.ExcelOption[]
  isCompact?: boolean
  orderManagementDayRange: DayRange
  setOrderManagementDayRange: (value: DayRange) => void
  getFromStorage?: boolean
  selectedView: OrderManagementGridView
  setSelectedView: (view: OrderManagementGridView) => void
}

export function DefaultActionBtns({
  orderStatus,
  onStatusChange,
  filters,
  setFilters,
  excelOptions,
  isCompact,
  orderManagementDayRange,
  setOrderManagementDayRange,
  getFromStorage,
  selectedView,
  setSelectedView,
}: DefaultActionBtnsProps) {
  const queryClient = useQueryClient()
  const options = [
    {
      key: 'all',
      value: 'all',
      label: (
        <>
          <Horizontal className='mt-2' gap={4} fullHeight verticalCenter>
            <span className='all dot mr-1' data-cy='All Filter Button' />
            <span>{pageConfig?.all?.title.replace('Orders', '')}</span>
          </Horizontal>
          <Divider className='p-0 m-1' />
        </>
      ),
    },
    {
      key: 'all_open',
      value: 'all_open',
      label: (
        <Horizontal className='mt-2' gap={4} fullHeight verticalCenter>
          <span className='all_open dot mr-1' data-cy='Open Filter Button' />
          <span>{pageConfig?.all_open?.title.replace('Orders', '')}</span>
        </Horizontal>
      ),
    },
    {
      key: 'accepted',
      value: 'accepted',
      label: (
        <div className='ml-4 open-option' data-cy='Accepted Filter Button'>
          {pageConfig?.accepted?.title.replace('Orders', '')}
        </div>
      ),
    },
    {
      key: 'assigned',
      value: 'assigned',
      label: (
        <div className='ml-4 open-option' data-cy='Assigned Filter Button'>
          {pageConfig?.assigned?.title.replace('Orders', '')}
        </div>
      ),
    },
    {
      key: 'in progress',
      value: 'in progress',
      label: (
        <>
          <Horizontal className='mt-2' gap={4} fullHeight verticalCenter data-cy='In Progress Filter Button'>
            <span className='in_progress dot mr-1' />
            <span>{pageConfig?.['in progress']?.title.replace('Orders', '')}</span>
          </Horizontal>
          <Divider className='p-0 m-1' />
        </>
      ),
    },
    {
      key: 'completed',
      value: 'completed',
      label: (
        <>
          <Horizontal className='mt-2' gap={4} fullHeight verticalCenter>
            <span className='completed dot mr-1' data-cy='Completed Filter Button' />
            <span>{pageConfig?.completed?.title.replace('Orders', '')}</span>
          </Horizontal>
          <Divider className='p-0 m-1' />
        </>
      ),
    },
    {
      key: 'canceled',
      value: 'canceled',
      label: (
        <Horizontal className='mt-2' gap={4} fullHeight verticalCenter>
          <span className='invalid dot mr-1' data-cy='Canceled Filter Button' />
          <span>{pageConfig?.canceled?.title.replace('Orders', '')}</span>
        </Horizontal>
      ),
    },
  ]

  return (
    <Horizontal gap={8} fullHeight verticalCenter>
      <OrderTankToggle selectedView={selectedView} setSelectedView={setSelectedView} />
      <Select style={{ width: 160 }} value={orderStatus} onChange={onStatusChange} options={options} />
      {!isCompact && (
        <UploadDownloadButtons
          excelOptions={excelOptions}
          refreshCallback={() =>
            queryClient.invalidateQueries({
              queryKey: ['getOrdersQuery'],
            })
          }
        />
      )}
      <DateRangeFilter
        currentFilters={filters}
        setCurrentFilters={setFilters}
        getFromStorage={getFromStorage}
        dayRangeStorage={orderManagementDayRange}
        setDayRangeStorage={setOrderManagementDayRange}
      />
    </Horizontal>
  )
}
